import exp from "node:constants";
import {Blocks} from "@/components/Common/Interfaces";

/**
 * Takes a Date object and returns a string formatted as YYYYMMDD.
 * @param date - The Date object to format.
 * @returns A string representing the date in YYYYMMDD format.
 */
export const formatDateToYYYYMMDD = (date: Date | string): string => {

    if (date instanceof Date) {

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return `${year}${month}${day}`;
    }

    return date;
};

/**
 * Takes a in the YYYYMMDD format and converts it in to a date object
 * @param date - The Date object to format.
 * @returns A string representing the date in YYYYMMDD format.
 */
export const parseDateFromYYYYMMDD = (date: string): Date => {
    const year = parseInt(date.substring(0, 4), 10);
    const month = parseInt(date.substring(4, 6), 10) - 1;
    const day = parseInt(date.substring(6, 8), 10);

    return new Date(year, month, day);
};

/**
 * Calculate the block end date based on start and number of weeks. Returned as a date object.
 *
 * @param startDate
 * @param spanLength
 */
export const calculateEndDate = (startDate : Date, spanLength : number) => {
    const endDate = new Date(startDate);
    const length = spanLength - 1;
    endDate.setDate(endDate.getDate() + length * 7); // Add the number of days corresponding to the spanLength in weeks
    return endDate;
};

/**
 * Function to figure out the non-working dates based on the passed in numerical representations of the days of the week.
 * Be default will get the next years worth.
 *
 * @param workingDays
 * @param numberOfWeeks
 * @param baseDate
 */
export const getDatesForNonWorkingDays = (workingDays: number[] = [], numberOfWeeks: number = 52, baseDate: Date = new Date()): string[] => {
    const dates: string[] = [];

    // Adjusted to treat Monday as 0 and Sunday as 6
    const allDays = [0, 1, 2, 3, 4, 5, 6]; // Monday to Sunday

    // Determine non-working days by excluding the working days
    const nonWorkingDays = allDays.filter(day => !workingDays.includes(day));

    // Get the first day of the month
    const baseMonday = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);

    // Adjust to Monday, making sure that the first day of the month is aligned with Monday = 0
    const dayOfWeek = (baseMonday.getDay() + 6) % 7; // Monday becomes 0
    baseMonday.setDate(baseMonday.getDate() - dayOfWeek); // Move to the Monday of the current week

    // Loop through the weeks to calculate non-working days
    for (let week = 0; week < numberOfWeeks; week++) {
        nonWorkingDays.forEach(day => {
            const currentDay = new Date(baseMonday);
            currentDay.setDate(baseMonday.getDate() + day + week * 7); // Add the day and week offset
            dates.push(formatDateToYYYYMMDD(currentDay));
        });
    }

    return dates;
};

/**
 * Format times from HH:mm to h:mm A, we can't rely on dayJs to do this so we need to do it ourselves
 *
 * @param time
 */
export const formatTime = (time: string) => {
    if (!time) return 'Invalid Time';
    const [hours, minutes] = time.split(':');

    // Convert to 12-hour format
    const hour = (parseInt(hours) % 12) || 12; // Convert 0 to 12
    const ampm = parseInt(hours) < 12 ? 'AM' : 'PM';

    return `${hour}:${minutes} ${ampm}`;
};


/**
 * Get all blocks with the same linked id as the passed in block.
 * @param blocks
 * @param linkedId
 */
const getAllBlocksWithLinkedId = (blocks: Blocks[], linkedId: string): Blocks[] => {
    return blocks.filter(block => block.linked_id === linkedId);
};

export default getAllBlocksWithLinkedId;
